/**
 * HelpDrawer - Slide-in help drawer accessible from GlobalHeader.
 *
 * Provides quick access to help content without leaving the current page.
 * Shows contextual help based on the current route.
 *
 * Features:
 * - Search functionality
 * - Contextual sections based on current route
 * - Links to full help page for deep dives
 * - Collapsible accordion sections
 */

import clsx from "clsx";
import { X, Search, ChevronDown, Lightbulb, ArrowRight } from "lucide-react";
import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

import { TERMINOLOGY } from "../../config/terminology";
import {
  helpSections,
  searchSections,
  type HelpSection,
} from "../../content/helpContent";

// =============================================================================
// TYPES
// =============================================================================

interface HelpDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

// =============================================================================
// ROUTE TO HELP MAPPING
// =============================================================================

/**
 * Map route patterns to relevant help section IDs.
 */
const routeToHelpSections: Record<string, string[]> = {
  // Existing routes (updated to use real section IDs)
  campaigns: ["start-campaign", "segmentation"],
  audiences: ["segmentation"],
  events: ["events"],
  forecasting: ["forecasting"],
  "donor-lab": ["donor-lab"],
  config: [
    "configure-brand-profile",
    "configure-content-quality",
    "onboard-client",
  ],
  settings: ["configure-content-quality", "onboard-client"],

  // New routes — Room → Surface framing
  "story-bank": ["stories-story-bank", "stories-worksheets"],
  "story-engine": ["stories-worksheets", "stories-story-bank"],
  "story-opportunities": ["stories-story-bank", "donors-portfolio"],
  meetings: ["meetings-meeting-prep"],
  "major-donors": ["donors-portfolio", "stories-story-bank"],
  "brand-profile": ["configure-brand-profile"],
};

/**
 * Get relevant help sections for the current route.
 */
function getContextualSections(pathname: string): HelpSection[] {
  // Extract the tool section from the path
  const match = pathname.match(/\/(?:clients|w)\/[^/]+\/([^/]+)/);
  const section = match?.[1];

  if (section && routeToHelpSections[section]) {
    const relevantIds = routeToHelpSections[section];
    return helpSections.filter((s) => relevantIds.includes(s.id));
  }

  // Default: show getting started sections
  return helpSections.filter((s) => s.category === "getting-started");
}

// =============================================================================
// ACCORDION SECTION
// =============================================================================

interface AccordionItemProps {
  section: HelpSection;
  expanded: boolean;
  onToggle: () => void;
}

function AccordionItem({ section, expanded, onToggle }: AccordionItemProps) {
  return (
    <div className="border-b border-[var(--ws-border-soft)] last:border-b-0">
      <button
        onClick={onToggle}
        className={clsx(
          "w-full flex items-center justify-between gap-3 px-4 py-3",
          "text-left hover:bg-[var(--ws-surface-subtle)]",
          "transition-colors",
        )}
      >
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-[var(--ws-text)]">
            {section.title}
          </h3>
          <p className="text-xs text-[var(--ws-text-secondary)] line-clamp-1 mt-0.5">
            {section.summary}
          </p>
        </div>
        <ChevronDown
          className={clsx(
            "h-4 w-4 text-[var(--ws-text-secondary)] flex-shrink-0",
            "transition-transform",
            expanded && "rotate-180",
          )}
        />
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3">
          {/* Steps */}
          {section.steps && section.steps.length > 0 && (
            <div className="space-y-2">
              {section.steps.slice(0, 3).map((step, idx) => (
                <div key={idx} className="flex gap-2">
                  <span
                    className={clsx(
                      "flex-shrink-0 w-5 h-5 rounded-full",
                      "bg-[var(--ws-accent-soft)] text-[var(--ws-accent)]",
                      "text-xs font-medium flex items-center justify-center",
                    )}
                  >
                    {idx + 1}
                  </span>
                  <div>
                    <span className="text-sm text-[var(--ws-text)]">
                      {step.title}
                    </span>
                  </div>
                </div>
              ))}
              {section.steps.length > 3 && (
                <p className="text-xs text-[var(--ws-text-secondary)] ml-7">
                  +{section.steps.length - 3} more steps
                </p>
              )}
            </div>
          )}

          {/* Tips preview */}
          {section.tips && section.tips.length > 0 && (
            <div
              className={clsx(
                "flex items-start gap-2 p-2 rounded-lg",
                "bg-amber-50 border border-amber-100",
              )}
            >
              <Lightbulb className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 line-clamp-2">
                {section.tips[0]}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function HelpDrawer({ isOpen, onClose }: HelpDrawerProps) {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when drawer opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      setExpandedSections(new Set());
      // Focus search input after animation
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Get sections to display
  const displaySections = useMemo(() => {
    if (searchQuery) {
      return searchSections(searchQuery);
    }
    return getContextualSections(location.pathname);
  }, [searchQuery, location.pathname]);

  // Toggle section expansion
  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className={clsx(
          "fixed top-0 right-0 z-50",
          "w-full max-w-md h-full",
          "bg-[var(--ws-surface)] border-l border-[var(--ws-border)]",
          "shadow-2xl",
          "flex flex-col",
          "animate-slide-in-right",
        )}
        role="dialog"
        aria-label="Help"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-[var(--ws-border)]">
          <h2 className="text-base font-semibold text-[var(--ws-text)]">
            Help
          </h2>
          <button
            onClick={onClose}
            className={clsx(
              "p-2 rounded-lg -mr-2",
              "text-[var(--ws-text-tertiary)]",
              "hover:bg-[var(--ws-surface-subtle)] hover:text-[var(--ws-text)]",
              "transition-colors",
            )}
            aria-label="Close help"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-[var(--ws-border-soft)]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--ws-text-secondary)]" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search help topics..."
              className={clsx(
                "w-full pl-9 pr-3 py-2 rounded-lg",
                "bg-[var(--ws-surface-subtle)] border border-transparent",
                "text-sm text-[var(--ws-text)]",
                "placeholder:text-[var(--ws-text-secondary)]",
                "focus:outline-none focus:border-[var(--ws-accent)]",
              )}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Context label */}
          {!searchQuery && (
            <div className="px-4 py-2 bg-[var(--ws-surface-subtle)]">
              <span className="text-xs font-medium text-[var(--ws-text-secondary)]">
                Relevant to this page
              </span>
            </div>
          )}

          {/* Sections */}
          {displaySections.length > 0 ? (
            <div>
              {displaySections.map((section) => (
                <AccordionItem
                  key={section.id}
                  section={section}
                  expanded={expandedSections.has(section.id)}
                  onToggle={() => toggleSection(section.id)}
                />
              ))}
            </div>
          ) : (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-[var(--ws-text-secondary)]">
                No help topics found for "{searchQuery}"
              </p>
              <button
                onClick={() => setSearchQuery("")}
                className="mt-2 text-xs text-[var(--ws-accent)] hover:underline"
              >
                Clear search
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-[var(--ws-border)]">
          <Link
            to="/help"
            onClick={onClose}
            className={clsx(
              "flex items-center justify-between px-3 py-2.5 rounded-lg",
              "bg-[var(--ws-surface-subtle)]",
              "text-sm text-[var(--ws-text)]",
              "hover:bg-[var(--ws-accent-soft)] hover:text-[var(--ws-accent)]",
              "transition-colors",
            )}
          >
            <span>View full Help Center</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
          {/* Version */}
          <div className="mt-3 pt-3 border-t border-[var(--ws-border-soft)] text-center">
            <span className="text-[10px] text-[var(--ws-text-secondary)]">
              {TERMINOLOGY.platform.name} v{TERMINOLOGY.platform.version}
            </span>
          </div>
        </div>
      </aside>

      {/* Animation styles */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.2s ease-out;
        }
      `}</style>
    </>
  );
}

export default HelpDrawer;
